import React, { useEffect, useState } from 'react';
import { Card } from '../../ui/card';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import { Badge } from '../../ui/badge';
import { Download } from 'lucide-react';
import { toast } from 'sonner';
import { ledgerApi, apiError } from '../../../lib/api';
import { AccountSelect, acct, DateField, downloadCSV, Empty, money, PageTitle, SOURCE_LABEL, today, useAccounts, yearStart } from './shared';

export default function Ledger() {
  const [accounts] = useAccounts();
  const [accountId, setAccountId] = useState('');
  const [from, setFrom] = useState(yearStart());
  const [to, setTo] = useState(today());
  const [gl, setGl] = useState(null);

  useEffect(() => {
    if (!accountId && accounts.length) setAccountId((accounts.find((a) => a.type === 'Bank') || accounts[0]).id);
  }, [accounts, accountId]);

  useEffect(() => {
    if (!accountId) return;
    ledgerApi.generalLedger(accountId, from, to).then(setGl).catch((e) => toast.error(apiError(e, 'Could not load the ledger')));
  }, [accountId, from, to]);

  const csv = () => downloadCSV(`ledger-${gl.account.name}-${from}-to-${to}.csv`, ['Date', 'No.', 'Source', 'Memo', 'Debit', 'Credit', 'Balance'],
    [['', '', '', 'Opening balance', '', '', gl.opening], ...gl.rows.map((r) => [r.date, r.number, SOURCE_LABEL[r.source] || r.source, r.memo, r.debit || '', r.credit || '', r.running])]);

  return (
    <div className="p-6 lg:p-8 space-y-5" data-testid="acct-ledger">
      <PageTitle title="General Ledger" subtitle="Every posting to one account, with its running balance.">
        <div>
          <Label className="text-xs text-slate-500">Account</Label>
          <AccountSelect accounts={accounts} value={accountId} onChange={setAccountId} className="mt-1 h-9 w-72" testId="gl-account" />
        </div>
        <DateField label="From" value={from} onChange={setFrom} />
        <DateField label="To" value={to} onChange={setTo} />
        {gl ? <Button variant="outline" className="h-9 gap-1.5" onClick={csv}><Download className="w-4 h-4" />CSV</Button> : null}
      </PageTitle>
      <Card className="border-slate-200">
        {!gl ? <Empty>Pick an account.</Empty> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs text-slate-500 font-semibold uppercase tracking-wider">
                <tr><th className="px-4 py-3 text-left">Date</th><th className="px-4 py-3 text-left">No.</th><th className="px-4 py-3 text-left">Source</th><th className="px-4 py-3 text-left">Memo</th><th className="px-4 py-3 text-right">Debit</th><th className="px-4 py-3 text-right">Credit</th><th className="px-4 py-3 text-right">Balance</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100 tabular-nums">
                <tr className="bg-slate-50/60"><td className="px-4 py-2 text-slate-500" colSpan={6}>Opening balance on {from}</td><td className="px-4 py-2 text-right font-semibold">{acct(gl.opening)}</td></tr>
                {gl.rows.map((r, i) => (
                  <tr key={`${r.entryId}-${i}`} className="hover:bg-slate-50">
                    <td className="px-4 py-2 text-slate-600 whitespace-nowrap">{r.date}</td>
                    <td className="px-4 py-2 text-slate-600">{r.number}</td>
                    <td className="px-4 py-2"><Badge className="border-0 bg-slate-100 text-slate-600">{SOURCE_LABEL[r.source] || r.source}</Badge></td>
                    <td className="px-4 py-2 text-slate-800">{r.memo}</td>
                    <td className="px-4 py-2 text-right">{r.debit ? money(r.debit) : ''}</td>
                    <td className="px-4 py-2 text-right">{r.credit ? money(r.credit) : ''}</td>
                    <td className="px-4 py-2 text-right font-semibold">{acct(r.running)}</td>
                  </tr>
                ))}
                <tr className="border-t-2 border-slate-900 font-extrabold"><td className="px-4 py-3" colSpan={6}>Closing balance on {to}</td><td className="px-4 py-3 text-right">{acct(gl.closing)}</td></tr>
              </tbody>
            </table>
            {!gl.rows.length ? <Empty>No postings to this account in the period.</Empty> : null}
          </div>
        )}
      </Card>
    </div>
  );
}
